import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly defaultTitle = 'Home Services in Dubai | Maid, Cleaning, Salon, AC & Repairs';
  private readonly defaultDescription = 'Book reliable home services in Dubai with YellowZap. Maid cleaning, home salon, AC service, electricians, plumbers & more at best prices.';
  private readonly defaultImage = 'https://firebasestorage.googleapis.com/v0/b/yellochat-prod.appspot.com/o/YC-LOGO.png?alt=media&token=476e6370-e9b6-4883-9428-71aefa23a264';
  private readonly siteName = 'YellowZap';
  private readonly locale = 'en_AE';

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private meta: Meta,
    private title: Title
  ) { }

  createLinkForCanonicalURL() {
    this.setCanonicalUrl(this.doc.URL);
  }

  updateTags(options: SeoMetaOptions = {}) {
    const title = this.cleanText(options.title) || this.defaultTitle;
    const description = this.cleanText(options.description) || this.defaultDescription;
    const image = options.image || this.defaultImage;
    const url = this.normalizeUrl(options.url || options.canonical || this.doc.URL);
    const type = options.type || 'website';
    const robots = options.robots || (options.noIndex ? 'noindex, nofollow' : 'index, follow');
    const twitterCard = options.twitterCard || 'summary_large_image';

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: robots });

    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:type', content: type });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });
    this.meta.updateTag({ property: 'og:locale', content: this.locale });
    this.meta.updateTag({ property: 'og:image', content: image });

    this.meta.updateTag({ name: 'twitter:card', content: twitterCard });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonicalUrl(url);
  }

  syncFromDocument(options: SeoMetaOptions = {}) {
    const title = this.title.getTitle();
    const description = this.meta.getTag('name="description"')?.content;
    this.updateTags({
      ...options,
      title: this.cleanText(title) || options.title,
      description: this.cleanText(description) || options.description
    });
  }

  setCanonicalUrl(url?: string) {
    const canonicalUrl = this.normalizeUrl(url || this.doc.URL);
    let link = this.doc.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }

  buildCanonicalUrl(routerUrl: string, baseUrl: string = environment.baseUrl): string {
    const cleanPath = (routerUrl || '/').split('?')[0].split('#')[0];
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    return this.normalizeUrl(`${normalizedBase}${normalizedPath}`);
  }

  private normalizeUrl(url: string): string {
    try {
      const parsed = new URL(url, this.doc.location.origin);
      parsed.search = '';
      parsed.hash = '';
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private cleanText(value?: string | null): string {
    return (value ?? '').trim();
  }

}

export interface SeoMetaOptions {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  canonical?: string;
  type?: string;
  robots?: string;
  twitterCard?: string;
  noIndex?: boolean;
}
